// src/components/dashboard/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/components/sidebar.css';
import logo from '../../assets/images/logo.jpeg';

const Sidebar = ({ toggled: propToggled, setToggled }) => {
  // Use both prop-based and local state for toggle
  const [toggled, setToggledState] = useState(propToggled);
  const [utilitiesOpen, setUtilitiesOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const location = useLocation();
  const { user, logout } = useAuth();
  const [subscriptionPlanName, setSubscriptionPlanName] = useState('');


  // Sync with prop
  useEffect(() => {
    setToggledState(propToggled);
  }, [propToggled]);

  useEffect(() => {
    if (user && user.SubscriptionType) {
      getPlanNameById(user.SubscriptionType)
        .then(name => setSubscriptionPlanName(name));
    }
  }, [user]);

  // Handle resize events for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      // If transitioning between mobile and desktop, ensure correct state
      if (mobile !== isMobile) {
        if (mobile) {
          // When switching to mobile, we want the sidebar to be hidden (toggled)
          if (!toggled) {
            handleToggle();
          }
        } else {
          // When switching to desktop, reset body class
          document.body.classList.remove('sidebar-open');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check

    return () => {
      window.removeEventListener('resize', handleResize);
      // Clean up body class when component unmounts
      document.body.classList.remove('sidebar-open');
    };
  }, [toggled, isMobile]);

  // Handle route changes
  useEffect(() => {
    // Auto-close sidebar on navigation for mobile
    if (isMobile && !toggled) {
      handleToggle();
    }

    // Auto-open relevant menu sections
    const isUtilityPath = [
      '/my-files',
      '/my-banking',
      '/my-passwords',
      '/endowment-centre',
      '/my-blockchain'
    ].includes(location.pathname);

    const isProfilePath = location.pathname === '/profile';

    setUtilitiesOpen(isUtilityPath);
    setProfileOpen(isProfilePath);
  }, [location, isMobile]);

  // Determine if a path is active
  const isActive = (path) => location.pathname === path;

  // Check if endowment center should be hidden based on subscription
  const hideEndowmentCenter = () => {
    return user && (user.SubscriptionType === "1" || user.SubscriptionType === "3");
  };

  const getPlanNameById = async (planId) => {
    const planNames = {
      '1': 'Basic',
      '2': 'Plus',
      '3': 'Trial'
    };

    return planNames[planId] || 'Standard';
  };

  const getPlanClass = (planName) => {
    switch (planName.toLowerCase()) {
      case 'plus':
        return 'premium';
      case 'basic':
        return 'standard';
      case 'trial':
        return 'trial';
      default:
        return 'standard';
    }
  };

  const toggleUtilities = (e) => {
    e.preventDefault();
    setUtilitiesOpen(!utilitiesOpen);
  };

  const toggleProfile = (e) => {
    e.preventDefault();
    setProfileOpen(!profileOpen);
  };

  const handleToggle = () => {
    // Only allow toggling on mobile devices
    if (isMobile) {
      const newToggledState = !toggled;
      setToggledState(newToggledState);

      // Propagate to parent if setToggled is provided
      if (setToggled) {
        setToggled(newToggledState);
      }

      // For mobile, add/remove body class to help with layout adjustments
      if (newToggledState) {
        document.body.classList.remove('sidebar-open');
      } else {
        document.body.classList.add('sidebar-open');
      }
    }
  };


  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    // Only use saved toggle state on mobile
    if (isMobile) {
      const savedToggleState = localStorage.getItem('sidebarToggled') === 'true';
      if (savedToggleState !== toggled) {
        setToggledState(savedToggleState);
        if (setToggled) {
          setToggled(savedToggleState);
        }
      }
    } else {
      // On desktop, always set to expanded (not toggled)
      setToggledState(false);
      if (setToggled) {
        setToggled(false);
      }
      document.body.classList.add('sidebar-expanded');
      document.body.classList.remove('sidebar-collapsed');
    }

    return () => {
      document.body.classList.remove('sidebar-expanded', 'sidebar-collapsed');
    };
  }, [isMobile]);

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && !toggled && (
        <div
          className="sidebar-overlay"
          onClick={handleToggle}
          aria-hidden="true"
        />
      )}



      <div
        className={`sidebar ${toggled ? 'toggled' : ''} ${isMobile ? 'mobile' : ''}`}
        id="accordionSidebar"
      >
        {/* User avatar and info for mobile - visible when expanded */}
        {isMobile && !toggled && (
          <div className="mobile-user-info">
            <div className="user-avatar">
              <i className="fas fa-user-circle"></i>
            </div>
            <div className="user-details">
              <div className="user-name">{user?.name || 'User'}</div>
              <div className="user-email">{user?.email || 'user@example.com'}</div>
            </div>
          </div>
        )}

        {/* Sidebar - Brand */}
        <Link className="sidebar-brand d-flex align-items-center justify-content-center" to="/dashboard">
          <div className="sidebar-brand-icon">
            <img src={logo} alt="PhēnixLoc Logo" className="logo-image" />
          </div>
          <div className="sidebar-brand-text">PhēnixLoc</div>
        </Link>

        {/* Divider */}
        <hr className="sidebar-divider" />

        {/* Nav Items Container */}
        <div className="nav-items-container">
          {/* Dashboard with notification badge */}
          <div className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}>
            <Link className="nav-link" to="/dashboard">
              <i className="fas fa-fw fa-tachometer-alt"></i>
              <span>Dashboard</span>
            </Link>
          </div>

          {/* Divider */}
          <hr className="sidebar-divider" />

          {/* Nav Item - Utilities Collapse Menu */}
          <div className="nav-item">
            <a
              className={`nav-link ${!utilitiesOpen ? 'collapsed' : ''}`}
              href="#"
              onClick={toggleUtilities}
              aria-expanded={utilitiesOpen}
              aria-controls="collapseUtilities"
            >
              <i className="fas fa-fw fa-cogs"></i>
              <span>Utilities</span>
              <i className={`fas fa-angle-${utilitiesOpen ? 'down' : 'right'} toggle-icon`}></i>
            </a>
            <div
              className={`collapse-menu ${utilitiesOpen ? 'show' : ''}`}
              id="collapseUtilities"
              aria-hidden={!utilitiesOpen}
            >
              <div className="collapse-items">
                <Link className={`collapse-item ${isActive('/my-files') ? 'active' : ''}`} to="/my-files">
                  <i className="fas fa-fw fa-file-alt submenu-icon"></i>
                  My Files
                </Link>
                <Link className={`collapse-item ${isActive('/my-banking') ? 'active' : ''}`} to="/my-banking">
                  <i className="fas fa-fw fa-credit-card submenu-icon"></i>
                  My Banking
                </Link>
                <Link className={`collapse-item ${isActive('/my-passwords') ? 'active' : ''}`} to="/my-passwords">
                  <i className="fas fa-fw fa-lock submenu-icon"></i>
                  My Passwords
                </Link>
                <Link className={`collapse-item ${isActive('/my-blockchain') ? 'active' : ''}`} to="/my-blockchain">
                  <i className="fas fa-fw fa-link submenu-icon"></i>
                  My Blockchain
                </Link>
                {!hideEndowmentCenter() && (
                  <Link className={`collapse-item ${isActive('/endowment-centre') ? 'active' : ''}`} to="/endowment-centre">
                    <i className="fas fa-fw fa-shield-alt submenu-icon"></i>
                    Endowment-Centre
                  </Link>
                )}
                
              </div>
            </div>
          </div>

          <div className="nav-item">
            <a
              className={`nav-link ${!profileOpen ? 'collapsed' : ''}`}
              href="#"
              onClick={toggleProfile}
              aria-expanded={profileOpen}
              aria-controls="collapsePages"
            >
              <i className="fas fa-fw fa-user-circle"></i>
              <span>My Profile</span>
              <i className={`fas fa-angle-${profileOpen ? 'down' : 'right'} toggle-icon`}></i>
            </a>
            <div
              className={`collapse-menu ${profileOpen ? 'show' : ''}`}
              id="collapsePages"
              aria-hidden={!profileOpen}
            >
              <div className="collapse-items">
                <Link className={`collapse-item ${isActive('/profile') ? 'active' : ''}`} to="/profile">
                  <i className="fas fa-fw fa-user submenu-icon"></i>
                  My Profile
                </Link>
                <div className="collapse-divider"></div>
                <div className="collapse-header">More:</div>
                <a
                  className="collapse-item"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.confirm('Are you sure you want to logout?')) {
                      logout();
                    }
                  }}
                >
                  <i className="fas fa-fw fa-sign-out-alt submenu-icon"></i>
                  Logout
                </a>
              </div>
            </div>
          </div>

          {/* Divider */}
          <hr className="sidebar-divider" />

          {/* Support and Info Section */}
          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <span>SUPPORT & INFO</span>
            </div>

            <div className={`nav-item ${isActive('/faq') ? 'active' : ''}`}>
              <Link className="nav-link" to="/faq">
                <i className="fas fa-fw fa-question"></i>
                <span>FAQ</span>
              </Link>
            </div>

            <div className={`nav-item ${isActive('/contact') ? 'active' : ''}`}>
              <Link className="nav-link" to="/contact">
                <i className="fas fa-fw fa-phone"></i>
                <span>Contact Us</span>
              </Link>
            </div>

            <div className="nav-item">
              <a
                className="nav-link"
                href="https://www.finnovant.com/about-us/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-fw fa-info-circle"></i>
                <span>About Us</span>
                <i className="fas fa-external-link-alt external-link-icon"></i>
              </a>
            </div>

            <div className="nav-item">
              <a
                className="nav-link"
                href="https://e02083be-e560-4942-b9e7-f69e17d904fd.filesusr.com/ugd/4d37f4_adcd1a757f1f4f838a4220c88acab79e.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-fw fa-user-secret"></i>
                <span>Privacy Policy</span>
                <i className="fas fa-external-link-alt external-link-icon"></i>
              </a>
            </div>
          </div>
        </div>



        {/* Subscription Status Indicator */}
        <div className="subscription-indicator">
          <div className="subscription-status">
            <span className={`status-dot ${getPlanClass(subscriptionPlanName)}`}></span>
            <span className="status-text">{subscriptionPlanName}</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;